/**
 * Anonymous terrace identity. A handle from the prefix/suffix pools plus an
 * app-issued UUID key, both minted once and persisted locally — the golazo
 * playerKey idiom. No platform session is ever involved.
 */

export const PREFIXES = [
  'NorthBank',
  'ClockEnd',
  'Highbury',
  'TheGun',
  'Emirates',
  'Ashburton',
  'Islington',
  'Arsenal',
] as const;

export const SUFFIXES = [
  'Nelson',
  'Cyrus',
  'Charlie',
  'Henry',
  'Adams',
  'Bergkamp',
  'Vieira',
  'Pires',
  'Lauren',
  'Cole',
] as const;

const HANDLE_KEY = 'coyg_handle';
const ANON_KEY = 'coyg_anon';

export function generateHandle(random: () => number = Math.random): string {
  const pre = PREFIXES[Math.floor(random() * PREFIXES.length)] ?? 'NorthBank';
  const suf = SUFFIXES[Math.floor(random() * SUFFIXES.length)] ?? 'Nelson';
  return pre + suf;
}

export function getHandle(): string {
  try {
    let h = localStorage.getItem(HANDLE_KEY);
    if (!h) {
      h = generateHandle();
      localStorage.setItem(HANDLE_KEY, h);
    }
    return h;
  } catch {
    return 'NorthBankNelson';
  }
}

export function getAnonKey(): string {
  try {
    let k = localStorage.getItem(ANON_KEY);
    if (!k) {
      k = crypto.randomUUID();
      localStorage.setItem(ANON_KEY, k);
    }
    return k;
  } catch {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    return `ephemeral-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
  }
}

/** Community mutations require a key that survives reloads so the supporter
 * can later delete, export or erase what they publish. */
export function getWritableAnonKey(): string | null {
  try {
    let key = localStorage.getItem(ANON_KEY);
    if (!key) {
      key = crypto.randomUUID();
      localStorage.setItem(ANON_KEY, key);
    }
    return localStorage.getItem(ANON_KEY) === key ? key : null;
  } catch {
    return null;
  }
}

/** Compose-bar truncation, exactly as designed. */
export function shortHandle(handle: string): string {
  return handle.length > 12 ? handle.slice(0, 11) + '…' : handle;
}

export const MIN_HANDLE = 3;
export const MAX_HANDLE = 20;

/** Mirror of the server's normalizer: collapse whitespace, trim ends. */
export function normalizeHandle(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

/**
 * Client mirror of the server's `isAllowedHandle` for instant feedback. The
 * server remains the authority on uniqueness and profanity; this just spares
 * a round-trip on obviously-wrong input.
 */
export function isValidCustomHandle(raw: string): boolean {
  const h = normalizeHandle(raw);
  const words = h.toLowerCase().replace(/[^a-z0-9]+/g, ' ');
  const blocked = /\b(?:n[i1]gg(?:er|a)|f[a4]gg[o0]t|k[i1]ke|p[a4]ki|c[u*]nt)\b/.test(words);
  const reserved = /(?:official|admin|moderator|staff|support)$/i.test(h) || /^coyg$/i.test(h);
  return h.length >= MIN_HANDLE && h.length <= MAX_HANDLE && /^[A-Za-z0-9][A-Za-z0-9 _-]*$/.test(h) && !blocked && !reserved;
}

/** Persist a chosen handle locally. The server claim is the source of truth. */
export function storeHandle(handle: string): void {
  try {
    localStorage.setItem(HANDLE_KEY, handle);
  } catch {
    /* private mode — handle stays ephemeral for this session */
  }
}
